/**
 * API клієнт для doc.material_transfer_for_processing — Передача сировини в переробку.
 * Permission: production.material_transfer.manage.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface MaterialTransferItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  account_code?: string | null;           // Счет учета (деф.201)
  transfer_account_code?: string | null;  // Счет передачи (деф.206)
}

export interface MaterialTransferItem extends MaterialTransferItemIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface MaterialTransferListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  processor_contractor_id: string;
  source_warehouse_id: string;
  total_cost: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3c:
  state?: CancellableState;
}

export interface MaterialTransfer extends MaterialTransferListItem {
  contract_id: string | null;
  expected_return_date: string | null;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  items: MaterialTransferItem[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface MaterialTransferCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  processor_contractor_id: string;
  contract_id?: string | null;
  source_warehouse_id: string;
  expected_return_date?: string | null;
  comment?: string | null;
  project_id?: string | null;
  items?: MaterialTransferItemIn[];
}

export type MaterialTransferUpdate = Partial<Omit<MaterialTransferCreate, 'organization_id'>>;

export const materialTransfersForProcessing = {
  list: (params?: {
    organization_id?: string;
    processor_contractor_id?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<MaterialTransferListItem>>('/material-transfers-for-processing', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<MaterialTransfer>(`/material-transfers-for-processing/${id}`).then((r) => r.data),
  create: (payload: MaterialTransferCreate) =>
    api.post<MaterialTransfer>('/material-transfers-for-processing', payload).then((r) => r.data),
  update: (id: string, payload: MaterialTransferUpdate) =>
    api.patch<MaterialTransfer>(`/material-transfers-for-processing/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/material-transfers-for-processing/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<MaterialTransfer>(`/material-transfers-for-processing/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<MaterialTransfer>(`/material-transfers-for-processing/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/material-transfers-for-processing/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
